import {
  CheckCircle2,
  ChevronRight,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";
import { TableState } from "../../../../shared/components/TableState";
import { RequestDetail } from "../../RequestDetail";
import {
  formatDisplayDate,
} from "../../model";
import { RequestDocuments } from "../shared/RequestDocuments";
import type {
  useRequestsWorkspace,
} from "./useRequestsWorkspace";

export function RequestWorkspace({ model }: { model: ReturnType<typeof useRequestsWorkspace>; }) {
  const {
    notify,
    user,
    onOpenRequestWorkspace,
    setStatusFilter,
    rows,
    setReloadKey,
    detailRequestNo,
    requestDetail,
    requestDetailLoading,
    requestDetailError,
    openRequest,
    openRequestDetail,
    closeRequestDetail,
    detailRequest,
    detailRequestCompleted,
  } = model;
  return (<>
    {user.role === "SATIN_ALMA" && detailRequestNo ? (
      createPortal(
        <div className="manager-dossier-modal-backdrop specialist-workspace-backdrop">
          <section
            className="manager-dossier-modal specialist-request-workspace"
            role="dialog"
            aria-modal="true"
            aria-labelledby="specialist-workspace-title"
          >
            <div className="manager-dossier-modal-bar">
              <div className="manager-dossier-modal-heading">
                <div className="manager-dossier-modal-context">
                  <span>Satın alma çalışma dosyası</span>
                  <i aria-hidden="true" />
                  <strong>{detailRequestNo}</strong>
                  {detailRequest?.category ? (
                    <>
                      <i aria-hidden="true" />
                      <strong>{detailRequest.category}</strong>
                    </>
                  ) : null}
                </div>
                <h2 id="specialist-workspace-title">
                  {detailRequest?.title ?? detailRequestNo}
                </h2>
              </div>
              <div className="manager-dossier-modal-controls">
                <button
                  type="button"
                  className="manager-dossier-close"
                  onClick={closeRequestDetail}
                >
                  Kapat <X size={17} aria-hidden="true" />
                </button>
              </div>
            </div>
            <div className="specialist-request-workspace-body">
              <RequestDetail
                requestNo={detailRequestNo}
                onBack={closeRequestDetail}
                notify={notify}
                user={user}
                presentation="workspace"
                onRequestRoutedForApproval={() => {
                  setStatusFilter("in-approval");
                  setReloadKey((current) => current + 1);
                }}
              />
            </div>
          </section>
        </div>,
        document.body,
      )
    ) : user.role !== "PLANLAMA" && detailRequestNo ? (
      <section
        className="form-modal-surface request-summary-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-summary-title"
      >
        <div className="section-heading compact">
          <div>
            <span className="section-kicker">
              {detailRequestCompleted
                ? "Tamamlanan satın alma"
                : "Talep ayrıntısı"}
            </span>
            <h2 id="request-summary-title">
              {requestDetail?.title ?? detailRequestNo}
            </h2>
            <p className="modal-description">
              {detailRequestCompleted
                ? `${detailRequestNo} numaralı kaydın sonuç ve denetim bilgileri`
                : `${detailRequestNo} numaralı talebin planlama bilgileri`}
            </p>
          </div>
          <button
            type="button"
            className="icon-button"
            aria-label="Talep ayrıntılarını kapat"
            onClick={closeRequestDetail}
          >
            <X size={17} />
          </button>
        </div>
        <div className="request-summary-modal-body">
          {requestDetailLoading ? (
            <TableState
              title="Talep ayrıntıları yükleniyor"
              description="Planlama kaydı ve süreç bilgileri alınıyor."
            />
          ) : requestDetailError ? (
            <TableState
              title="Talep ayrıntıları yüklenemedi"
              description={requestDetailError}
              action={
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => openRequestDetail(detailRequestNo)}
                >
                  Yeniden dene
                </button>
              }
            />
          ) : requestDetail ? (
            <>
              {detailRequestCompleted ? (
                <div className="request-summary-completed-state">
                  <CheckCircle2 size={19} aria-hidden="true" />
                  <div>
                    <strong>Süreç tamamlandı</strong>
                    <span>
                      Bu kayıt salt okunur. Yeni işlem başlatılamaz.
                    </span>
                  </div>
                </div>
              ) : null}
              <div className="request-summary-lead">
                <span>{requestDetail.category}</span>
                <strong>
                  {requestDetail.description || "Açıklama girilmemiş."}
                </strong>
              </div>
              <dl className="request-summary-grid">
                <div>
                  <dt>Miktar</dt>
                  <dd>
                    {requestDetail.quantity} {requestDetail.unit}
                  </dd>
                </div>
                <div>
                  <dt>İhtiyaç tarihi</dt>
                  <dd>{formatDisplayDate(requestDetail.neededAt)}</dd>
                </div>
                <div>
                  <dt>Talep eden</dt>
                  <dd>{requestDetail.requester}</dd>
                </div>
                <div>
                  <dt>Departman</dt>
                  <dd>{requestDetail.department}</dd>
                </div>
                <div>
                  <dt>Öncelik</dt>
                  <dd>{requestDetail.priority}</dd>
                </div>
                <div>
                  <dt>Durum</dt>
                  <dd>{requestDetail.status}</dd>
                </div>
              </dl>
              <RequestDocuments
                attachments={requestDetail.attachments}
                offers={requestDetail.offers}
              />
            </>
          ) : null}
        </div>
        {requestDetail ? (
          <div className="request-summary-modal-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={closeRequestDetail}
            >
              Kapat
            </button>
            {detailRequestCompleted ? (
              <button
                type="button"
                className="secondary-button request-summary-history-action"
                onClick={() => {
                  const requestNo = detailRequestNo;
                  closeRequestDetail();
                  onOpenRequestWorkspace(requestNo, "offers");
                }}
              >
                Teklifleri ve karar geçmişini görüntüle
                <ChevronRight size={15} />
              </button>
            ) : (
              <button
                type="button"
                className="primary-button"
                onClick={() => {
                  const request = rows.find(
                    (item) => item.id === detailRequestNo,
                  );
                  closeRequestDetail();
                  if (request) openRequest(request);
                }}
              >
                İşleme geç <ChevronRight size={15} />
              </button>
            )}
          </div>
        ) : null}
      </section>
    ) : null}
  </>);
}
